/**
 * Birkenstock × Wunder — GİRİŞ GEREKTİRMEYEN stok & satış sayfası.
 *
 * platform.glowytics.com/p/<gizli-slug> — sidebar YOK, marka seçici YOK,
 * üstte sadece Glowytics logosu.
 *
 * 🔴🔴 KAPSAM KURALI (28 Ağu 2026, Efe): sayfayı Birkenstock görüyor. Yalnızca
 * kendi ürünlerinin stoğu ve satışı gösterilir. Mağazanın toplam cirosu,
 * Birkenstock'un mağaza içindeki payı ve reklam/kampanya verisinin tamamı
 * bilerek çıkarılmıştır — bunlar Wunder'ın özel bilgisi. "Bağlam olsun" diye
 * mağaza geneli bir metrik ya da kampanya kutusu EKLEME.
 *
 * Veri tek uçtan gelir: GET /api/public-reports/<gizli-slug> (auth yok, 15 dk
 * sunucu cache'i). Slug config/publicReportLinks.js'ten okunur — backend'deki
 * WUNDER_BIRKENSTOCK_SLUG ile aynı olmak ZORUNDA.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, RefreshCw, Info } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { WUNDER_BIRKENSTOCK_SLUG } from '../../config/publicReportLinks';

const API_BASE = 'https://api.glowytics.com';

const GREEN = '#0F9D58';
const AMBER = '#B45309';
const INK = '#111827';

/* ── biçimlendirme ─────────────────────────────────────────────────── */
const int = (v) => Number(v || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 });
const dec = (v, d = 2) => Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });
const tl = (v, d = 0) => '₺' + dec(v, d);
const AY = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const GUN = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
const gunAy = (iso) => {
  if (!iso) return '';
  const [, m, d] = iso.split('-').map(Number);
  return `${d} ${AY[m - 1]}`;
};
const gunAyGun = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ${AY[m - 1]} ${GUN[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]}`;
};
const saat = (v) => (v
  ? new Date(String(v).replace(' ', 'T')).toLocaleString('tr-TR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
  : null);

/* ── küçük bileşenler ──────────────────────────────────────────────── */
function Card({ title, subtitle, children }) {
  return (
    <section className="bg-white rounded-xl border border-gray-200">
      {(title || subtitle) && (
        <header className="px-4 sm:px-5 pt-4 pb-3 border-b border-gray-100">
          {title && <h2 className="text-[15px] font-semibold text-gray-900">{title}</h2>}
          {subtitle && <p className="text-xs text-gray-500 mt-1 leading-relaxed">{subtitle}</p>}
        </header>
      )}
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  );
}

function Kpi({ label, value, hint, accent }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3.5 py-3">
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums" style={{ color: accent || INK }}>{value}</div>
      {hint && <div className="mt-0.5 text-[11px] text-gray-400 leading-snug">{hint}</div>}
    </div>
  );
}

/** Yatay kaydırılabilir tablo sarıcısı — dar ekranda sayfa gövdesi kaymasın. */
const Scroller = ({ children }) => (
  <div className="overflow-x-auto -mx-4 sm:-mx-5 px-4 sm:px-5">{children}</div>
);

function SalesTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2 text-xs">
      <div className="font-semibold text-gray-900 mb-1">{gunAyGun(label)}</div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-gray-500">Satılan</span>
        <span className="font-medium tabular-nums">{int(p.items)} adet</span>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-gray-500">Ciro</span>
        <span className="font-medium tabular-nums" style={{ color: GREEN }}>{tl(p.revenue, 2)}</span>
      </div>
    </div>
  );
}

/* ── sayfa ─────────────────────────────────────────────────────────── */
export default function WunderBirkenstockPublic() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // credentials YOK: uç zaten public, çerez göndermek CORS'u gereksiz sıkar.
      const r = await fetch(`${API_BASE}/api/public-reports/${WUNDER_BIRKENSTOCK_SLUG}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setD(await r.json());
    } catch (e) {
      setError(e.message || 'Veri alınamadı');
      setD(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Gizli adresin gizli kalmasının tek yolu link disiplini değil: sayfa bir kez
  // indekslenirse arama sonucundan bulunur. noindex + referrer kapalı → linke
  // tıklanan dış siteye adres sızmaz. Sayfadan çıkınca etiketler geri alınır.
  useEffect(() => {
    document.title = 'Birkenstock · Stok ve Satış';
    const tags = [
      { name: 'robots', content: 'noindex, nofollow, noarchive' },
      { name: 'referrer', content: 'no-referrer' },
    ].map(({ name, content }) => {
      const el = document.createElement('meta');
      el.setAttribute('name', name);
      el.setAttribute('content', content);
      document.head.appendChild(el);
      return el;
    });
    return () => tags.forEach((el) => el.remove());
  }, []);

  const daily = d?.sales?.daily || [];
  const stock = d?.stock;
  const total = d?.sales?.total;

  // Stok o günkü anlık görüntüden okunuyor. Ölçüm alınmayan günde sütun BOŞ
  // kalır — 0 yazmak "stok bitti" demek olurdu, oysa o gün ölçülmedi.
  const stokGun = useMemo(
    () => new Map((stock?.daily || []).map((r) => [r.date, r.units])),
    [stock]
  );

  // 30 günde her günün etiketi eksene sığmıyor; seyrekleştir.
  const tickInterval = useMemo(() => (daily.length > 20 ? 4 : 1), [daily.length]);

  const generatedAt = d?.generatedAt
    ? new Date(d.generatedAt).toLocaleString('tr-TR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="min-h-screen bg-[#F6F8FF]">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1100px] mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-3">
          <img src="/Calisma-Yuzeyi-20@5x-4.png" alt="Glowytics" className="h-7 w-auto" />
          <div className="flex items-center gap-3 text-xs text-gray-400">
            {generatedAt && <span className="hidden sm:inline">Son güncelleme: {generatedAt}</span>}
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              title="Yeniden yükle"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-3 sm:px-6 py-5 space-y-5">
        {error && (
          <div className="flex items-start gap-3 p-4 rounded-xl border border-red-200 bg-red-50 text-sm text-red-700">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">Rapor yüklenemedi</div>
              <div className="text-red-600 mt-0.5">{error}</div>
            </div>
          </div>
        )}

        {loading && !d && <div className="text-sm text-gray-400 py-20 text-center">Rapor hazırlanıyor…</div>}

        {d && (
          <>
            {/* ── başlık ───────────────────────────────────────────── */}
            <section className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="text-[11px] uppercase tracking-wide text-gray-400">Wunder online mağaza</div>
              <h1 className="mt-1 text-2xl font-semibold text-gray-900">Birkenstock · Stok ve satış</h1>
              <p className="mt-2 text-sm text-gray-600">
                Son {d.range.days} gün · {gunAy(d.range.start)} – {gunAy(d.range.end)}
              </p>
            </section>

            {/* ── özet ─────────────────────────────────────────────── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi
                label="Kalan stok"
                value={`${int(stock.units)} adet`}
                hint={stock.date ? `${gunAyGun(stock.date)} itibarıyla` : null}
              />
              <Kpi
                label="Stokta olan ürün"
                value={`${int(stock.inStock)} / ${int(stock.products)}`}
                hint="Katalogdaki Birkenstock ürünleri"
              />
              <Kpi
                label="Tükenen ürün"
                value={int(stock.outOfStock)}
                accent={stock.outOfStock > 0 ? AMBER : INK}
                hint="Katalogda var, stoğu sıfır"
              />
              <Kpi
                label={`Son ${d.range.days} gün satış`}
                value={`${int(total.items)} adet`}
                accent={GREEN}
                hint={`${int(total.orders)} sipariş · ${tl(total.revenue, 2)}`}
              />
            </div>

            {/* ── günlük satış ─────────────────────────────────────── */}
            <Card title="Günlük satış" subtitle={`Sütun: o gün satılan adet · ${gunAy(d.range.start)} – ${gunAy(d.range.end)}`}>
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={daily} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
                    <CartesianGrid stroke="#EEF1F7" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={gunAy} interval={tickInterval}
                           tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} width={32} />
                    <Tooltip content={<SalesTooltip />} cursor={{ fill: '#F3F6FD' }} />
                    <Bar dataKey="items" fill={GREEN} radius={[4, 4, 0, 0]} maxBarSize={26} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <Scroller>
                <table className="w-full text-sm mt-4 min-w-[520px]">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                      <th className="text-left font-medium py-2 pr-3">Gün</th>
                      <th className="text-right font-medium py-2 px-2">Sipariş</th>
                      <th className="text-right font-medium py-2 px-2">Satılan adet</th>
                      <th className="text-right font-medium py-2 px-2">Ciro</th>
                      <th className="text-right font-medium py-2 pl-2">Kalan stok</th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {daily.map((r) => {
                      const partial = r.date === d.sales.partialDay;
                      return (
                        <tr key={r.date} className="border-b border-gray-100 last:border-0">
                          <td className="py-2 pr-3 text-gray-700 whitespace-nowrap">
                            {gunAyGun(r.date)}
                            {partial && <span className="ml-1.5 text-[10px] text-amber-600">kısmi</span>}
                          </td>
                          <td className="py-2 px-2 text-right text-gray-600">{r.orders ? int(r.orders) : '—'}</td>
                          <td className="py-2 px-2 text-right text-gray-600">{r.items ? int(r.items) : '—'}</td>
                          <td className="py-2 px-2 text-right font-medium" style={{ color: r.revenue ? GREEN : '#9CA3AF' }}>
                            {r.revenue ? tl(r.revenue, 2) : '—'}
                          </td>
                          <td className="py-2 pl-2 text-right text-gray-600">
                            {stokGun.has(r.date) ? `${int(stokGun.get(r.date))} adet` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="border-t-2 border-gray-300 font-semibold">
                      <td className="py-2 pr-3 text-gray-900">Toplam</td>
                      <td className="py-2 px-2 text-right">{int(total.orders)}</td>
                      <td className="py-2 px-2 text-right">{int(total.items)}</td>
                      <td className="py-2 px-2 text-right" style={{ color: GREEN }}>{tl(total.revenue, 2)}</td>
                      <td className="py-2 pl-2 text-right">{int(stock.units)} adet</td>
                    </tr>
                  </tbody>
                </table>
              </Scroller>
              {stock.daily && stock.daily.length < 2 && (
                <div className="mt-3 flex items-start gap-3 p-3.5 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-900">
                  <Info className="w-4 h-4 shrink-0 mt-0.5 opacity-70" />
                  <div className="leading-relaxed">
                    <div className="font-semibold mb-0.5">
                      “Kalan stok” kolonu neden boş?
                    </div>
                    Bugünün stoğu ölçüldü ve elimizde: <b>{int(stock.units)} adet</b>. Eksik olan
                    <b> geçmiş</b> — günlük stok kaydı {gunAy(stock.seriesRestart)} tarihinde başladı,
                    daha önceki günler için stok ölçümü bulunmuyor. Kolon her sabah bir gün daha
                    dolacak. <b>Satış geçmişi eksiksizdir</b>, tablodaki satış rakamları 30 günün tamamını kapsar.
                  </div>
                </div>
              )}
              <p className="mt-2 text-[11px] text-gray-400 leading-relaxed">
                Bugünün satırı kısmidir; gün kapanmadı{d.sales.syncedAt ? ` ve satış aktarımı en son ${saat(d.sales.syncedAt)}'de çalıştı` : ''}.
                Ciro satılan adet × satır fiyatıdır (indirim sonrası); iadeler düşülmemiştir.
                “Kalan stok” o gün alınan stok ölçümüdür; ölçüm alınmayan günlerde boştur.
              </p>
            </Card>

            {/* ── yöntem ───────────────────────────────────────────── */}
            <Card title="Veri ve yöntem">
              <ul className="text-sm text-gray-600 space-y-2 leading-relaxed list-disc list-inside">
                <li>
                  <b className="text-gray-900">Satış</b>, Wunder'ın online mağazasındaki
                  {' '}<b>{d.brand}</b> siparişleridir. Fiziksel mağaza satışları dâhil değildir.
                </li>
                <li>
                  <b className="text-gray-900">Stok</b>, günde bir kez alınan anlık görüntüdür
                  {stock.date && ` (son alınan: ${gunAyGun(stock.date)})`}; gün içindeki hareketler
                  bir sonraki güne yansır.
                </li>
                <li>
                  Sayfa canlıdır ve kendini günceller; en fazla 15 dakikalık bir kopya gösterir.
                </li>
              </ul>
            </Card>

            <div className="text-center text-xs text-gray-400 pb-8">
              Glowytics · Birkenstock × Wunder{generatedAt && ` · ${generatedAt}`}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
